import { Flex } from '@assets/component/UI/Flex/Flex';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import {
    dbPathsNarrative,
    NarrativeNode,
    NarrativeStore,
    TopologyNode
} from '@assets/model/NarrativeStore';
import { SideChatMessage } from '@assets/model/SideChatStore';
import { useStore, useStoreUI } from '@assets/model/Store';
import classNames from 'classnames';
import { push, ref, set } from 'firebase/database';
import _ from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import FlLogo from '../FlightlineChatLogo.svg';
import * as style from './RecommendL3.module.less';
import { MessageReasoningFooter } from '../shared/MessageReasoningFooter';

interface RecommendL3MessageProps {
    message: SideChatMessage;
    narStore: NarrativeStore;
}

interface L3_Hypothesis {
    hypothesis: string;
    reasoning: string;
    ui_display: string;
}

const RecommendL3Message = observer((p: RecommendL3MessageProps) => {
    const { message: m, narStore } = p;
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!m) {
        return <div>RecommendL3 Message: Missing Message</div>;
    }

    const list = m.content as L3_Hypothesis[];

    if (!list || currentIndex >= list.length) {
        return <div>RecommendL3 Message: Index Error</div>;
    }
    const onClickNext = () => {
        setCurrentIndex((currentIndex + 1) % list.length);
    };

    const onAddL3 = async (entry: L3_Hypothesis) => {
        const store = useStore();
        const uiStore = useStoreUI();

        const fb = getFirebase();
        const pathBuilder = new dbPathsNarrative(store.selectedReportId);
        const path = pathBuilder.narrativeA1_Nodes();
        const nodes_ref = ref(fb.db, path);
        const insert_ref = push(nodes_ref);

        const newEntry: NarrativeNode = {
            nodeId: insert_ref.key,
            hypothesis: entry.hypothesis,
            ui_display: entry.ui_display
        };

        const parentId = m.fullChatContext.split(':').at(-1); //Should be L2

        narStore.pushTopologyHistory(
            `New L3: Adding ${insert_ref.key} to ${parentId}`
        );

        set(insert_ref, newEntry);

        const topologyNode = _.cloneDeep(toJS(narStore.topologyNodeRoot));

        function findNodeById(
            node: TopologyNode,
            findId: string,
            recursion_limit_track = 0
        ): TopologyNode {
            if (recursion_limit_track > 5) {
                throw new Error('Recursion Error: findNodeById');
            }

            if (node.nodeId == findId) {
                return node;
            }
            for (const entry of node.children) {
                const ret = findNodeById(
                    entry,
                    findId,
                    recursion_limit_track + 1
                );
                if (ret) {
                    return ret;
                }
            }
            return null;
        }

        const parentNode = findNodeById(topologyNode, parentId);
        const newNodeTopology: TopologyNode = {
            nodeId: insert_ref.key,
            children: []
        };

        parentNode.children.push(newNodeTopology);
        const topologyPath = pathBuilder.narrativeA1_Topology();
        let topo_ref = ref(fb.db, topologyPath);
        set(topo_ref, topologyNode);
    };

    const buildHypothesisEl = (entry: L3_Hypothesis) => {
        return (
            <div className={style.l3Grid}>
                <div className={classNames('item', style.l3_header)}>
                    <Flex>
                        <div style={{ flex: 1 }}>
                            ACTION: Add Level-3 Hypothesis
                        </div>
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                onClickNext();
                            }}
                        >
                            {currentIndex + 1}/{list.length}
                            <i className="bi bi-chevron-double-right"></i>
                        </div>
                    </Flex>
                </div>

                <div className={classNames('item', style.l3_text)}>
                    {entry.hypothesis}
                </div>
                <div className={classNames('item', style.cta_button)}>
                    <Button
                        variant="light"
                        className={style.denyButton}
                        onClick={() => {
                            onClickNext();
                        }}
                    >
                        <i className="bi bi-x"></i>
                        Discard
                    </Button>
                    <Button
                        variant="light"
                        className={style.approveButton}
                        onClick={() => {
                            onAddL3(entry);
                        }}
                    >
                        <i className="bi bi-check"></i>
                        Approve
                    </Button>
                </div>
                <MessageReasoningFooter m={m}>
                    {entry.reasoning}
                </MessageReasoningFooter>
            </div>
        );
    };

    const currentL3 = list[currentIndex];

    return (
        <Flex style={{ width: '100%' }}>
            <div className={style.logoBackgroundBase}>
                <img src={FlLogo}></img>
            </div>
            {buildHypothesisEl(currentL3)}
        </Flex>
    );
});

export default RecommendL3Message;
